import { AreaResponse } from '@doda/api-client/aspida/@types';

/**
 * 地域データ
 */
export interface AreaData {
  /** ID */
  id: string;
  /** 地域名 */
  name: string;
  /** 選択されているかどうかのフラグ */
  isChecked: boolean;
  /** チェックボックス表示のフラグ */
  isDisplay: boolean;
  /** 都道府県データ */
  prefectureList: PrefectureData[];
}

/**
 * 都道府県データ
 */
export interface PrefectureData {
  /** ID */
  id: string;
  /** 都道府県名 */
  name: string;
  /** 選択されているかどうかのフラグ */
  isChecked: boolean;
  /** チェックボックス表示のフラグ */
  isDisplay: boolean;
  /** 親の地域ID */
  parentId: string;
  /** 市区町村データ */
  cityList: CityData[];
}

/**
 * 市区町村データ
 */
export interface CityData {
  /** ID */
  id: string;
  /** 市区町村名 */
  name: string;
  /** 選択されているかどうかのフラグ */
  isChecked: boolean;
  /** チェックボックス表示のフラグ */
  isDisplay: boolean;
  /** 親の都道府県ID */
  parentId: string;
  /** 政令指定都市データ */
  wardList: WardData[];
}

/**
 * 政令指定都市データ
 */
export interface WardData {
  /** ID */
  id: string;
  /** 市区町村名 */
  name: string;
  /** 選択されているかどうかのフラグ */
  isChecked: boolean;
  /** チェックボックス表示のフラグ */
  isDisplay: boolean;
  /** 親の市区町村ID */
  parentId: string;
}

const numberToString = (arg: number) => {
  return arg.toString();
};

/**
 * 地域の配列を生成
 * @param masterData 勤務地マスタ
 * @param areaIdList 地域IDの配列
 * @param prefectureList 都道府県データ
 * @returns 地域データ
 */
export const createAreaData = (
  masterData: AreaResponse[],
  areaIdList: string[] | number[] | undefined,
  prefectureList: Omit<PrefectureData, 'cityList'>[]
): Omit<AreaData, 'prefectureList'>[] => {
  const areaIdListToString = areaIdList?.map(String);

  const prefectureParentIdList = [
    ...new Set(prefectureList.map((city) => city.parentId)),
  ];

  return masterData.flatMap(({ id, name }) => {
    const idToString = numberToString(id);
    const isDisplayValue = areaIdListToString?.includes(idToString) ?? false;

    return isDisplayValue || prefectureParentIdList.includes(idToString)
      ? [
          {
            id: idToString,
            name,
            isChecked: isDisplayValue,
            isDisplay: isDisplayValue,
          },
        ]
      : [];
  });
};

/**
 * 都道府県の配列を生成
 * @param masterData 勤務地マスタ
 * @param prefectureIdList 都道府県IDの配列
 * @param cityList 市区町村データ
 * @returns 都道府県データ
 */
export const createPrefectureData = (
  masterData: AreaResponse[],
  prefectureIdList: string[] | number[] | undefined,
  cityList: Omit<CityData, 'wardList'>[]
): Omit<PrefectureData, 'cityList'>[] => {
  const prefectureIdListToString = prefectureIdList?.map(String);

  const cityParentIdList = [...new Set(cityList.map((city) => city.parentId))];

  return masterData.flatMap((area) =>
    area.prefectureList.flatMap(({ id, name }) => {
      const idToString = numberToString(id);
      const isDisplayValue =
        prefectureIdListToString?.includes(idToString) ?? false;

      return isDisplayValue || cityParentIdList.includes(idToString)
        ? [
            {
              id: idToString,
              name,
              isChecked: isDisplayValue,
              isDisplay: isDisplayValue,
              parentId: numberToString(area.id),
            },
          ]
        : [];
    })
  );
};

/**
 * 市区町村の配列を生成
 * @param masterData 勤務地マスタ
 * @param cityIdList 市区町村IDの配列
 * @param wardList 政令指定都市データ
 * @returns 市区町村データ
 */
export const createCityData = (
  masterData: AreaResponse[],
  cityIdList: string[] | undefined,
  wardList: WardData[]
): Omit<CityData, 'wardList'>[] => {
  const wardParentIdList = [...new Set(wardList.map((ward) => ward.parentId))];

  return masterData.flatMap(({ prefectureList }) =>
    prefectureList.flatMap((prefecture) =>
      prefecture.cityList.flatMap(({ id, name }) => {
        const isDisplayValue = cityIdList?.includes(id) ?? false;

        return isDisplayValue || wardParentIdList.includes(id)
          ? [
              {
                id,
                name,
                isChecked: isDisplayValue,
                isDisplay: isDisplayValue,
                parentId: numberToString(prefecture.id),
              },
            ]
          : [];
      })
    )
  );
};

/**
 * 政令指定都市の配列を生成
 * @param masterData 勤務地マスタ
 * @param wardIdList 政令指定都市IDの配列
 * @returns 政令指定都市データ
 */
export const createWardData = (
  masterData: AreaResponse[],
  wardIdList: string[] | undefined
): WardData[] => {
  return masterData.flatMap(({ prefectureList }) =>
    prefectureList.flatMap(({ cityList }) =>
      cityList.flatMap((city) =>
        city.wardList.flatMap(({ id, name }) => {
          const isDisplayValue = wardIdList?.includes(id) ?? false;

          return isDisplayValue
            ? [
                {
                  id,
                  name,
                  isChecked: true,
                  isDisplay: true,
                  parentId: city.id,
                },
              ]
            : [];
        })
      )
    )
  );
};

interface CreateLocationDisplayListType {
  areaList: Omit<AreaData, 'prefectureList'>[];
  prefectureList: Omit<PrefectureData, 'cityList'>[];
  cityList: Omit<CityData, 'wardList'>[];
  wardList: WardData[];
}
/**
 * 表示用配列の生成処理
 * @param param.areaList 地域データ
 * @param param.prefectureList 都道府県データ
 * @param param.cityList 市区町村データ
 * @param param.wardList 政令指定都市データ
 * @returns 表示用の勤務地データ
 */
export const createLocationDisplayList = ({
  areaList,
  prefectureList,
  cityList,
  wardList,
}: CreateLocationDisplayListType): AreaData[] => {
  return areaList.map((area) => ({
    ...area,
    prefectureList: prefectureList
      .filter(({ parentId }) => parentId === area.id)
      .map((prefecture) => ({
        ...prefecture,
        cityList: cityList
          .filter(({ parentId }) => parentId === prefecture.id)
          .map((city) => ({
            ...city,
            wardList: wardList.filter(({ parentId }) => parentId === city.id),
          })),
      })),
  }));
};
